import Model, { attr } from '@warp-drive/legacy/model';
import { Type } from '@warp-drive/core-types/symbols';

/**
 * Command status type
 */
export type CommandStatus = 'success' | 'error' | 'pending' | 'running';

/**
 * Command error object structure
 */
export interface CommandError {
  detail: string;
  type?: string;
  code?: string;
  field?: string;
}

/**
 * Command metadata structure
 */
export interface CommandMetadata {
  requested_at: string;
  requested_by?: string | number;
  completed_at?: string;
  duration_ms?: number;
  [key: string]: unknown;
}

/**
 * Command model represents the response from backend command endpoints.
 *
 * Commands are actions that change system state (reindex, send_email, reseed, etc.)
 * following CQRS (Command Query Responsibility Segregation) principles.
 *
 * The backend returns a standardized format via the CommandResponse concern.
 *
 * @example
 * // Execute a command via adapter
 * const command = await this.store.adapterFor('command').execute('reindex-record', {
 *   model_type: 'Person',
 *   record_id: '123'
 * });
 *
 * if (command.isSuccess) {
 *   console.log(command.message);
 *   console.log(command.result);
 * } else {
 *   console.error(command.commandErrors);
 * }
 */
export default class CommandModel extends Model {
  /**
   * The command name in kebab-case (e.g., 'reindex-record', 'send-email')
   */
  @attr declare command: string;

  /**
   * Command execution status
   * - 'success': Command completed successfully
   * - 'error': Command failed
   * - 'pending': Command queued for async processing
   * - 'running': Command currently executing
   */
  @attr declare status: CommandStatus;

  /**
   * Human-readable message describing the result
   */
  @attr declare message: string;

  /**
   * Command-specific result data (flexible object)
   * Contains custom fields relevant to the specific command
   */
  @attr declare result: Record<string, unknown>;

  /**
   * Array of error objects from the command execution
   * Note: Named 'commandErrors' to avoid collision with Model's 'errors' property
   */
  @attr declare commandErrors: CommandError[];

  /**
   * Metadata about command execution
   */
  @attr declare metadata: CommandMetadata;

  /**
   * Returns true if the command was successful
   */
  get isSuccess(): boolean {
    return this.status === 'success';
  }

  /**
   * Returns true if the command failed
   */
  get isError(): boolean {
    return this.status === 'error';
  }

  /**
   * Returns true if the command is pending
   */
  get isPending(): boolean {
    return this.status === 'pending';
  }

  /**
   * Returns true if the command is running
   */
  get isRunning(): boolean {
    return this.status === 'running';
  }

  /**
   * Returns true if the command has any errors
   */
  get hasErrors(): boolean {
    return this.isError || this.commandErrors.length > 0;
  }

  /**
   * Returns array of error detail strings
   */
  get errorMessages(): string[] {
    return this.commandErrors.map((error) => error.detail);
  }

  /**
   * Returns a formatted error message for display
   * If single error, returns just the detail
   * If multiple errors, returns message + list of errors
   */
  get errorMessage(): string {
    if (this.commandErrors.length === 0) {
      return this.message;
    }
    if (this.commandErrors.length === 1) {
      return this.commandErrors[0]?.detail || this.message;
    }
    return `${this.message}: ${this.errorMessages.join(', ')}`;
  }

  /**
   * Returns the execution duration in milliseconds (if available)
   */
  get durationMs(): number | undefined {
    return this.metadata.duration_ms;
  }

  /**
   * Returns the execution duration in seconds (if available)
   */
  get durationSeconds(): number | undefined {
    return this.durationMs ? this.durationMs / 1000 : undefined;
  }

  /**
   * Returns when the command was requested as a Date object
   */
  get requestedAt(): Date | undefined {
    return this.metadata.requested_at ? new Date(this.metadata.requested_at) : undefined;
  }

  /**
   * Returns when the command completed as a Date object (if applicable)
   */
  get completedAt(): Date | undefined {
    return this.metadata.completed_at ? new Date(this.metadata.completed_at) : undefined;
  }

  [Type] = 'command' as const;
}
